import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Button } from "react-bootstrap";
import { debounce } from "lodash";
import { applyProtocolPlaceholders, showToast } from "main/shared/helpers";

const defaultToolbar = [
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link"],
  ["clean"],
];

const REQUIRED_TOKENS = ["[ROOM]", "[SAMPLE_TYPE]"];

const getPlainTextFromHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const getHtmlTextLength = (html) => {
  if (!html) return 0;
  return getPlainTextFromHtml(html).trim().length;
};

const hasRequiredTokens = (html) => {
  const plainText = getPlainTextFromHtml(html);
  return REQUIRED_TOKENS.every((token) => plainText.includes(token));
};

const getMissingTokens = (html) =>
  REQUIRED_TOKENS.filter(
    (token) => !getPlainTextFromHtml(html).includes(token)
  );

export default function ProtocolEditorForModal({
  text,
  onSave,
  maxLength = 20000,
  disabled = false,
  onCancle,
  toolbar = true,
  requireTokens = true,
  tokenReplacements = null,
}) {
  const resolveEditorText = useCallback(
    (value) => {
      const html = value || "";
      return tokenReplacements
        ? applyProtocolPlaceholders(html, tokenReplacements)
        : html;
    },
    [tokenReplacements]
  );

  // Use text as initial value to ensure fresh state on each mount
  const initialText = resolveEditorText(text);
  const [editorHtml, setEditorHtml] = useState(initialText);
  const [length, setLength] = useState(getHtmlTextLength(initialText));
  const editorHtmlProtocolRef = useRef(initialText);
  const quillRef = useRef(null);

  // CRITICAL: Always sync ref with text prop on mount and updates
  useEffect(() => {
    const nextText = resolveEditorText(text);
    editorHtmlProtocolRef.current = nextText;
    setEditorHtml(nextText);
    setLength(getHtmlTextLength(nextText));
  }, [text, resolveEditorText]);

  // Strip unwanted styles on paste - keep only allowed formatting
  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
        delta.ops.forEach((op) => {
          if (op.attributes) {
            // Keep only allowed attributes and flatten indents
            const newAttributes = {};

            if (op.attributes.bold) newAttributes.bold = op.attributes.bold;
            if (op.attributes.italic)
              newAttributes.italic = op.attributes.italic;
            if (op.attributes.underline)
              newAttributes.underline = op.attributes.underline;
            if (op.attributes.link) newAttributes.link = op.attributes.link;

            // Keep list type but remove indent
            if (op.attributes.list) {
              newAttributes.list = op.attributes.list;
            }

            op.attributes = newAttributes;
          }
        });
        return delta;
      });
    }
  }, []);

  const debouncedUpdate = useMemo(
    () =>
      debounce((content) => {
        editorHtmlProtocolRef.current = content;
        setLength(getHtmlTextLength(content));
      }, 200),
    []
  );

  useEffect(() => {
    return () => {
      debouncedUpdate.cancel();
    };
  }, [debouncedUpdate]);

  const handleChange = (content, _delta, source) => {
    if (source !== "user") {
      setEditorHtml(content);
      editorHtmlProtocolRef.current = content;
      debouncedUpdate(content);
      return;
    }

    const newLength = getHtmlTextLength(content);
    if (newLength <= maxLength) {
      setEditorHtml(content);
      editorHtmlProtocolRef.current = content;
      debouncedUpdate(content);
    } else {
      showToast("Character limit exceeded");
    }
  };

  const getCurrentEditorHtml = () => {
    if (quillRef.current) {
      return quillRef.current.getEditor().root.innerHTML;
    }
    return editorHtmlProtocolRef.current || editorHtml || "";
  };

  const initialHasTokensRef = useRef(hasRequiredTokens(text || ""));

  useEffect(() => {
    initialHasTokensRef.current = hasRequiredTokens(text || "");
  }, [text]);

  const handleSave = () => {
    debouncedUpdate.flush?.();
    let currentValue = getCurrentEditorHtml();

    if (
      requireTokens &&
      initialHasTokensRef.current &&
      !hasRequiredTokens(currentValue)
    ) {
      const missing = getMissingTokens(currentValue);
      showToast(
        `Protocol must include ${missing.join(" and ")} placeholder${
          missing.length > 1 ? "s" : ""
        }.`
      );
      return;
    }

    onSave?.(
      tokenReplacements
        ? applyProtocolPlaceholders(currentValue, tokenReplacements)
        : currentValue
    );
  };

  return (
    <>
      <ReactQuill
        ref={quillRef}
        id="protocol-quill"
        theme="snow"
        value={editorHtml}
        readOnly={disabled}
        onChange={handleChange}
        modules={{
          toolbar: toolbar ? defaultToolbar : false,
        }}
      />
      <div className="rightsideactions">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            width: "100%",
          }}
        >
          <small>
            {length}/{maxLength}
          </small>
          <div className="d-flex mt-3">
            <Button
              className="text-secondary500 bg-transparent border-0"
              onClick={onCancle}
              disabled={disabled}
            >
              Cancel
            </Button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={disabled}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
